import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import _ from 'lodash'
import cx from 'classnames'
import { Button, Modal } from 'react-bootstrap'

import { InlineResponse, PopUpResponse, SliderResponse } from '../responseContainer'
import { SeekSearchBox } from '../searchInput'
import { SeekContext, WidgetsContext } from '../../core'

import styles from './questionDesign.module.scss'

const QuestionComponent = ({ widgetProps, className, breakpoint }) => {
  const questionDesign = _.get(
    widgetProps,
    `properties.${breakpoint}.questionDesign`,
    {}
  )
  const seekLayoutType = _.get(questionDesign, `type`, 'bar')

  return (
    <React.Fragment>
      {seekLayoutType === 'bar' && (
        <BarLayoutComponent
          widgetProps={widgetProps}
          breakpoint={breakpoint}
          className={className}
        />
      )}
    </React.Fragment>
  )
}

const BarLayoutComponent = ({ widgetProps, breakpoint, className }) => {

  const seekContext = React.useContext(SeekContext)
  const context = React.useContext(WidgetsContext);

  const [state, setState] = useState({
    question: '',
    showChats: false
  })

  const id = _.get(widgetProps, 'uid')
  const seekSession = seekContext.seekSession || {}
  const pageSession = useSelector((state) =>
    _.get(state, 'pages.analytics.id', null)
  )
  const pageId = useSelector((state) =>
  _.get(state, 'pages.item.id', null)
)
  const widgetProperties = _.get(widgetProps, 'properties')
  const storyboard = _.get(context, 'storyboard.id');

  const seekResponseProps = _.get(
    widgetProperties,
    `${breakpoint}.responseDesign`,
    {}
  )

  const layoutType = _.get(
    seekResponseProps,
    'type',
    breakpoint === 'desktop' ? 'inline' : 'popup'
  )

  const handleSubmit = (question) => {
    let questionLength = question.replace(/\s+/g, '').length
    let seekQASessionId = _.get(seekSession, `${id}.item.id`, null)
    if (question && questionLength !== 0) {
      seekContext.seekAnswerForQuestion(
        id,
        seekQASessionId,
        question,
        pageSession,
        pageId,
        storyboard
      )

      setState((state) => {
        return {
          ...state,
          showChats: true,
          question: ''
        }
      })
    }
  }

  const handleSuggestionClick = (suggestion, disabled) => {
    if (!seekContext.debug || disabled) {
      return
    }

    let seekQASessionId = _.get(seekSession, `${id}.item.id`, null)
    seekContext.seekAnswerForQuestion(
      id,
      seekQASessionId,
      suggestion,
      pageSession,
      pageId,
      storyboard,
    )

    setState((state) => {
      return {
        ...state,
        showChats: true,
        question: ''
      }
    })
  }

  useEffect(()=>{
    let seekQASession = _.get(seekSession, `${id}.item`, null)

    if(layoutType !== 'inline') {
      return;
    }

    if(state.showChats && !seekQASession) {
      setState((state)=>{
        return {
          ...state,
          showChats: false
        }
      })
    }
  },[seekSession])

  const handleCloseChat = () => {
    setState((state) => {
      return {
        ...state,
        showChats: false,
      }
    })

    seekContext.invalidateSeekSession(id)
  }

  switch (layoutType) {
    case 'inline': {
      return (
        <div className={cx(styles.inlineContainer, className)}>
        { !state.showChats &&         
          <SeekSearchBox
            widgetProps={widgetProps}
            value={state.question}
            onChange={(value) => {
              setState((state) => {
                return {
                  ...state,
                  question: value
                }
              })
            }}
            className={cx(styles.searchInputField)}
            breakpoint={breakpoint}
            disabled={!seekContext.debug}
            handleSuggestionClick={handleSuggestionClick}
            onSubmit={handleSubmit}
          />}
          {state.showChats && (
            <InlineResponse
              widgetProps={widgetProps}
              onClose={handleCloseChat}
              show={state.showChats}
              breakpoint={breakpoint}
              handleSuggestionClick={handleSuggestionClick}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      )
    }

    case 'popup': {
      return (
        <div className={cx(styles.popupConatiner, className)}>
          <SeekSearchBox
            widgetProps={widgetProps}
            value={state.question}
            onChange={(value) => {
              setState((state) => {
                return {
                  ...state,
                  question: value
                }
              })
            }}
            className={cx(styles.searchInputField)}
            breakpoint={breakpoint}
            disabled={!seekContext.debug}
            handleSuggestionClick={handleSuggestionClick}
            onSubmit={handleSubmit}
          />
          {state.showChats && (
            <PopUpResponse
              widgetProps={widgetProps}
              onClose={handleCloseChat}
              show={state.showChats}
              breakpoint={breakpoint}
              handleSuggestionClick={handleSuggestionClick}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      )
    }

    case 'slider': {
      return (
        <div className={cx(styles.popupConatiner, className)}>
          <SeekSearchBox
            widgetProps={widgetProps}
            value={state.question}
            onChange={(value) => {
              setState((state) => {
                return {
                  ...state,
                  question: value
                }
              })
            }}
            className={cx(styles.searchInputField)}
            breakpoint={breakpoint}
            disabled={!seekContext.debug}
            handleSuggestionClick={handleSuggestionClick}
            onSubmit={handleSubmit}
          />
          {state.showChats && (
            <SliderResponse
              widgetProps={widgetProps}
              onClose={handleCloseChat}
              show={state.showChats}
              breakpoint={breakpoint}
              handleSuggestionClick={handleSuggestionClick}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      )
    }
  }
}

const questionConatinerEquals = function (prevProps, nextProps) {
  const paths = ['className', 'widgetProps', 'breakpoint']
  const next = _.pick(nextProps, paths)
  const previous = _.pick(prevProps, paths)

  const equals = _.isEqual(next, previous)
  return equals
}

export const QuestionDesign = React.memo(
  QuestionComponent,
  questionConatinerEquals
)
